import { useEffect, useState } from 'react';

import { activityTypes } from 'bundles/apex/activities/type/config';
import { checkDateChangeForImmediateReminders } from 'bundles/apex/reminders/helpers';
import { get } from 'helpers/api';
import { getParentOptionValue } from 'bundles/crm/parent_helpers';
import { CancelError, getRequestReference } from 'helpers/axios';
import { serializeTaskForm } from '../serializers';

import type { Dependencies, ForUser, ModalOptions, Task, TaskAttributes, TaskFormState } from '../types';
import type { Reminder } from 'bundles/apex/reminders/types';

import 'bundles/apex/tasks/styles.scss';
import { showSnackbar } from 'components/ui/snackbar/helper';

export interface Props {
  checkImmediateRemindersPath?: string;
  createTask?: (task: TaskAttributes) => Promise<any>;
  formDependenciesPath?: string;
  forUser?: ForUser;
  hideOnExternalElementClick?: boolean;
  reminders?: Reminder[];
  show?: boolean;
  updateTask?: (task: TaskAttributes) => Promise<any>;
  options?: ModalOptions;
  onHide: () => void;
}

type TaskSaveSerializer = () => Promise<Partial<TaskAttributes> | null>;

const nonClosingDataAttribute = 'data-apex-tasks-list__modal__non-closing';
export const nonClosingProps = { [nonClosingDataAttribute]: true }

let dependenciesRequestReference;

export function useTaskModalState({
  checkImmediateRemindersPath,
  createTask,
  formDependenciesPath,
  forUser,
  hideOnExternalElementClick,
  options,
  reminders,
  show,
  updateTask,
  onHide
}: Props) {
  const [dependencies, setDependencies] = useState<Dependencies | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(true);
  const [formTask, setFormTask] = useState<TaskFormState>({ contactsTasks: [] });
  const [requestReminders, setRequestReminders] = useState<Reminder[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<String[] | null>();
  const [immediateReminders, setImmediateReminders] = useState<any[]>([]);
  const [userNames, setUserNames] = useState<any[]>([]);
  const [recheckReminders, setRecheckReminders] = useState(false);
  const [originalDeadline, setOriginalDeadline] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [contactsTasks, setContactsTasks] = useState(formTask?.contactsTasks || []);

  useEffect(() => {
    if (hideOnExternalElementClick) {
      function onClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (!target?.closest(`[${nonClosingDataAttribute}]`)) {
          hide();
        }
      }

      document.body.classList.add('has-apex-task-modal');
      document.addEventListener('click', onClick, true);

      return () => {
        document.body.classList.remove('has-apex-task-modal');
        document.removeEventListener('click', onClick, true);
      };
    }
  }, []);

  useEffect(() => {
    // TODO: Smart caching would be nice - RTK Query? Custom hook? Need to be able to invalidate on date change...
    if (show) {
      (async () => {
        dependenciesRequestReference?.cancel();
        dependenciesRequestReference = getRequestReference();

        setLoading(true);

        try {
          const dependencies = await get(formDependenciesPath || '/apex/tasks/form_dependencies', {
            cancelToken: dependenciesRequestReference.token,
          });

          setDependencies(dependencies);
          setInitializing(false);
          setLoading(false);
        } catch (error) {
          if (!(error instanceof CancelError)) {
            showSnackbar({ text: 'Something went wrong', variant: 'danger' });
            hide();
          }
        }
      })();
    }
  }, [formDependenciesPath, show]);

  function hide() {
    setInitializing(true);
    onHide();
  }

  async function reset(task: Task | null = null) {
    let deadline = task?.deadline;
    if (!deadline) {
      if (dependencies?.deal) {
        deadline = { operator: 'before', units: 'day' };
      } else {
        deadline = { date: '', static: true };
      }
    }

    const formTask: TaskFormState = {
      ...task,
      assigneeId: task?.assignee?.id,
      contactsTasks: task?.contactsTasks?.map(contactsTask => ({ ...contactsTask, localId: contactsTask.id })) || [],
      deadline,
      requiresAttachment: task?.requiresAttachment ? 'true' : 'false',
      reminders: reminders || [],
    };

    if (task) {
      formTask.parentOptionValue = getParentOptionValue(task);
    } else if (options?.parents?.length) {
      formTask.parentOptionValue = options.parents[0].value;
    }

    setFormTask(formTask);
    setOriginalDeadline(formTask.deadline);

    setRequestReminders(reminders?.filter(reminder => !reminder.sent) || []);
    setImmediateReminders([]);
    setRecheckReminders(false);
    setUserNames([]);
    setErrors(null);
    setHasChanges(false);
  }

  function handleRemindersChange(updatedReminders: Reminder[]) {
    setHasChanges(true);
    setRecheckReminders(true);
    setRequestReminders(updatedReminders);
  }

  async function handleAdd(keepReminders: boolean | null | undefined = null) {
    const processFormTask = async () => {
      if (formTask.id && keepReminders == null && recheckReminders && checkImmediateRemindersPath) {
        const hasImmediateReminders = await checkForImmediateReminders(requestReminders);
        setRecheckReminders(false);
        if (hasImmediateReminders) {
          return null;
        }
      }

      return serializeTaskForm(formTask, { keepReminders, reminders: requestReminders });
    }

    return handleSave(processFormTask, false);
  }

  async function handleSave(serializeTask: TaskSaveSerializer, autosave: boolean, forceSave: boolean = false) {
    if (autosave && !hasChanges && !forceSave) {
      return;
    }

    setSaving(true);

    try {
      const task = await serializeTask();

      if (!task) {
        return;
      }

      let result;
      if (formTask.id && updateTask) {
        result = await updateTask(task);
      } else if (!formTask.id && createTask) {
        result = await createTask(task);
      }

      setRequestReminders(filterReminders(result?.reminders || []));
      setImmediateReminders([]);
      setContactsTasks(result?.contactsTasks || []);
      setHasChanges(false);
      setSaving(false);
      setErrors(null);
      !autosave && hide();
    } catch (errors) {
      setErrors(errors);
      setSaving(false);
    }
  }

  const filterReminders = (reminders: Reminder[]) => {
    const immediateReminderIds = immediateReminders.map(reminder => reminder.id);
    let filteredReminders = reminders
    
    if (immediateReminderIds.length > 0) {
      filteredReminders = filteredReminders.filter(reminder => !immediateReminderIds.includes(reminder.id));
    }

    if (forUser) {
      filteredReminders = filteredReminders.filter(reminder => reminder.userId === forUser.id);
    }

    return filteredReminders;
  }

  async function checkForImmediateReminders(reminders: Reminder[], deadline = formTask.deadline) {
    const result = await checkDateChangeForImmediateReminders(
      checkImmediateRemindersPath,
      deadline,
      requestReminders
    );

    if (result.reminders.length > 0) {
      setImmediateReminders(result.reminders);
      setUserNames(result.users);
      setSaving(false);
      return true;
    }

    return false;
  }

  async function handleAutosave(
    changes: Partial<TaskFormState>,
    keepReminders: boolean | null | undefined = null,
    forceSave: boolean = false
  ) {
    const processChanges = async () => {
      if (formTask.id) {
        let hasDeadlineChanged = false;
        if (changes.deadline && originalDeadline) {
          hasDeadlineChanged = JSON.stringify(originalDeadline) !== JSON.stringify(changes.deadline);
        }
        if ((recheckReminders || hasDeadlineChanged) && keepReminders == null && checkImmediateRemindersPath) {
          const hasImmediateReminders = await checkForImmediateReminders(
            changes.reminders || requestReminders,
            changes.deadline
          );
          setRecheckReminders(false);
          if (hasImmediateReminders) {
            return null;
          }
        }
      }

      const reminders = changes.reminders;
      const task = serializeTaskForm(
        { ...changes, parentOptionValue: formTask?.parentOptionValue },
        { keepReminders, reminders }
      );
      if (formTask?.id && formTask?.path) {
        task.id = formTask.id;
        task.path = formTask.path;
      } else if (!formTask?.id && !formTask?.name) {
        task.name ||= 'New Task';
      }
      return task;
    }
    return handleSave(processChanges, true, forceSave);
  }

  function handleChange(changes) {
    // Check if deadline is being changed
    if (changes.deadline && originalDeadline) {
      const hasChanged = JSON.stringify(originalDeadline) !== JSON.stringify(changes.deadline);
      setRecheckReminders(hasChanged);
    }

    setHasChanges(true);
    setFormTask({ ...formTask, ...changes });
  }

  const typeOptions = [
    { label: 'None', value: '' },
    ...Object.values(activityTypes).map(({ key, label }) => ({
      label,
      value: key,
    })),
  ];

  return ({
    contactsTasks,
    dependencies,
    errors,
    formTask,
    handleAdd,
    handleAutosave,
    handleChange,
    handleRemindersChange,
    hasChanges,
    hide,
    immediateReminders,
    initializing,
    loading,
    reset,
    requestReminders,
    saving,
    setContactsTasks,
    setImmediateReminders,
    typeOptions,
    userNames,
  });
}
